import { benefitValue, type Line } from './stocks';

/**
 * Stock planner (owner's suggestion 191801-8144: "which blocks to sell to fund better investments — accurate, based on
 * ROI; a little planner: sell X and buy Y, or hold X N more weeks until you can afford Y"). Pure.
 *
 * The money a stock pays is its benefit's value: money as it says, items at their market value (stocks.ts
 * benefitValue); other benefits (energy, discounts, passive ones) have no money value — they are never sold by a plan,
 * only their spare shares (shares above their last full block, which pay nothing).
 *
 * Active benefits stack: block n needs 2^(n-1) × the requirement in shares, and each block pays the benefit once per
 * period. So selling X's top block loses one payout per period; buying Y's next block adds one.
 *
 * The plans (money from a sale always buys the best yearly return for the money first, block after block — `reinvest`):
 *  - "spare":  sell shares that pay nothing (spare shares of any stock) and buy blocks — pure gain;
 *  - "swap":   sell X's top block (and X's spare shares) and buy blocks that together pay more a year than it did;
 *  - "wait":   a block that pays more than X's top block but costs more than it brings: keep collecting your money
 *              dividends N more weeks, then swap.
 */

/** Torn's fee for selling shares (0.1% of the sale). */
export const SELL_FEE = 0.001;

export interface Timing {
  /** A dividend is ready to collect now. */
  ready: boolean;
  /** Days until the next payout (null: not known). */
  days: number | null;
}

export interface PlanInput {
  lines: readonly Line[];
  /** An item's market value by its name (item dividends). */
  itemValue: (name: string) => number | null;
  /** Each stock's dividend timing, by stock id (optional: from Torn's page). */
  timing?: ReadonlyMap<number, Timing>;
}

export interface Buy {
  id: number;
  acronym: string;
  /** How many blocks of it in a row (each next one needs twice the shares). */
  blocks: number;
  cost: number;
  yearly: number;
}

export interface Plan {
  kind: 'spare' | 'swap' | 'wait';
  /** The stock whose top block is sold (swap/wait). */
  sell: { id: number; acronym: string; shares: number; money: number; yearly: number } | null;
  /** Spare shares sold (they pay nothing). */
  spare: Array<{ id: number; acronym: string; shares: number; money: number }>;
  /** What the money buys, the best return first (wait: the one block it saves up for). */
  buys: Buy[];
  /** Money a year more than now. */
  gain: number;
  /** Money left over after buying (≥ 0). */
  left: number;
  /** wait: weeks of dividends still needed. */
  weeks: number;
  /** wait: weeks it would take on dividends alone, keeping X (and its money a year). */
  alone: number;
  /** Collect X's dividend first / sell right after its payout in N days (null: no advice). */
  note: string | null;
}

/** One benefit payout per block, in money a year (null: no money value or not an active stock). */
export function yearlyPerBlock(l: Line, itemValue: (name: string) => number | null): number | null {
  if (l.stock.kind === 'passive' || !l.stock.frequency || !l.stock.requirement) return null;
  const v = benefitValue(l.stock.benefit, itemValue);
  return v === null || v <= 0 ? null : (v * 365) / l.stock.frequency;
}

/** Shares in each full block, lowest first (active: 1×, 2×, 4×… the requirement; passive: one block). */
export function blockShares(l: Line): number[] {
  const req = l.stock.requirement;
  if (!req) return [];
  if (l.stock.kind === 'passive') return l.blocks ? [req] : [];
  return Array.from({ length: l.blocks }, (_, i) => req * 2 ** i);
}

/** Shares above the last full block (they pay nothing). */
export function spareShares(l: Line): number {
  if (!l.stock.requirement) return 0; // no blocks known: every share may be what pays (or not) — leave them alone
  return Math.max(0, l.shares - blockShares(l).reduce((t, n) => t + n, 0));
}

const sale = (shares: number, price: number) => Math.floor(shares * price * (1 - SELL_FEE));
const weeks = (need: number, perWeek: number) => (need <= 0 ? 0 : perWeek > 0 ? Math.ceil(need / perWeek) : Infinity);

/**
 * Spend `money` on next blocks, the best yearly return for the money first, again and again while anything is
 * affordable (a stock's following block needs twice the shares). Never buys the stocks in `skip`.
 */
export function reinvest(money: number, lines: readonly Line[], per: ReadonlyMap<number, number | null>, skip: ReadonlySet<number>): { buys: Buy[]; left: number; yearly: number } {
  const next = new Map<number, { l: Line; shares: number; done: number }>();
  for (const l of lines) if (per.get(l.stock.id) && l.nextShares && !skip.has(l.stock.id)) next.set(l.stock.id, { l, shares: l.nextShares, done: 0 });
  const bought = new Map<number, Buy>();
  let left = money;
  let yearly = 0;
  for (let i = 0; i < 100; i++) {
    let best: { id: number; cost: number; roi: number } | null = null;
    for (const [id, n] of next) {
      const cost = Math.round(n.shares * n.l.stock.price);
      const roi = per.get(id)! / cost;
      if (cost <= left && (!best || roi > best.roi)) best = { id, cost, roi };
    }
    if (!best) break;
    const n = next.get(best.id)!;
    left -= best.cost;
    yearly += per.get(best.id)!;
    const b = bought.get(best.id) ?? { id: best.id, acronym: n.l.stock.acronym, blocks: 0, cost: 0, yearly: 0 };
    b.blocks++;
    b.cost += best.cost;
    b.yearly += per.get(best.id)!;
    bought.set(best.id, b);
    n.done++;
    n.shares = n.l.stock.requirement! * 2 ** (n.l.blocks + n.done);
  }
  return { buys: [...bought.values()], left, yearly }; // in the order first bought: the best return first
}

/** Every worthwhile plan, best first: plans you can do now (more money a year first), then ones that wait. */
export function stockPlans(input: PlanInput, maxWeeks = 26): Plan[] {
  const { lines, itemValue } = input;
  const per = new Map(lines.map((l) => [l.stock.id, yearlyPerBlock(l, itemValue)]));
  // Money a week from every money-paying block you hold (what "wait" saves up).
  const weekly = lines.reduce((t, l) => t + (per.get(l.stock.id) ?? 0) * l.blocks, 0) / 52;
  const spareOf = new Map(lines.map((l) => [l.stock.id, spareShares(l)]));
  const plans: Plan[] = [];

  // Spare shares (they pay nothing) → blocks of other stocks.
  const spares = lines.filter((l) => (spareOf.get(l.stock.id) ?? 0) > 0).map((l) => ({ id: l.stock.id, acronym: l.stock.acronym, shares: spareOf.get(l.stock.id)!, money: sale(spareOf.get(l.stock.id)!, l.stock.price) }));
  if (spares.length) {
    const r = reinvest(
      spares.reduce((t, x) => t + x.money, 0),
      lines,
      per,
      new Set(spares.map((x) => x.id)),
    );
    if (r.buys.length) plans.push({ kind: 'spare', sell: null, spare: spares, buys: r.buys, gain: r.yearly, left: r.left, weeks: 0, alone: 0, note: null });
  }

  for (const x of lines) {
    const xYear = per.get(x.stock.id);
    if (!xYear || !x.blocks) continue;
    const own = blockShares(x).at(-1)! + (spareOf.get(x.stock.id) ?? 0);
    const money = sale(own, x.stock.price);
    const sell = { id: x.stock.id, acronym: x.stock.acronym, shares: own, money, yearly: xYear };
    const t = input.timing?.get(x.stock.id);
    const note = t?.ready ? `collect ${x.stock.acronym}’s dividend first` : t?.days !== null && t?.days !== undefined && t.days <= 7 ? `sell ${x.stock.acronym} right after its payout in ${t.days} day${t.days === 1 ? '' : 's'}` : null;
    // Swap now: X's top block's money into the best returns.
    const r = reinvest(money, lines, per, new Set([x.stock.id]));
    if (r.buys.length && r.yearly > xYear) plans.push({ kind: 'swap', sell, spare: [], buys: r.buys, gain: r.yearly - xYear, left: r.left, weeks: 0, alone: 0, note });
    // Or wait: a block that pays more than X's top block but costs more than it brings — save up the dividends.
    for (const y of lines) {
      const yYear = per.get(y.stock.id);
      if (y.stock.id === x.stock.id || !yYear || !y.nextCost || yYear <= xYear || y.nextCost <= money) continue;
      const w = weeks(y.nextCost - money, weekly);
      if (w > maxWeeks) continue;
      plans.push({ kind: 'wait', sell, spare: [], buys: [{ id: y.stock.id, acronym: y.stock.acronym, blocks: 1, cost: y.nextCost, yearly: yYear }], gain: yYear - xYear, left: 0, weeks: w, alone: weeks(y.nextCost, weekly), note });
    }
  }
  // A block something you can do now already buys needs no waiting.
  const now = new Set(plans.filter((p) => p.kind !== 'wait').flatMap((p) => p.buys.map((b) => b.id)));
  return plans.filter((p) => p.kind !== 'wait' || !now.has(p.buys[0]!.id)).sort((a, b) => Number(a.kind === 'wait') - Number(b.kind === 'wait') || b.gain - a.gain || a.weeks - b.weeks);
}

/** The plans worth showing: one per stock sold (and per block waited for), at most `n`. */
export function topPlans(plans: readonly Plan[], n = 5): Plan[] {
  const seen = new Set<string>();
  const out: Plan[] = [];
  for (const p of plans) {
    const key = p.kind === 'wait' ? `w:${p.buys[0]!.id}` : `n:${p.sell?.id ?? 'spare'}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
    if (out.length >= n) break;
  }
  return out;
}

/** "Ready in 7 days" / "Ready in 1 day" / "Ready" → days (0 = ready); null for anything else. */
export function readyInDays(text: string | null | undefined): number | null {
  const t = String(text ?? '').trim();
  const m = /ready in\s+(\d+)\s*day/i.exec(t);
  if (m) return Number(m[1]);
  if (/ready in\s+\d+\s*h/i.test(t)) return 0;
  return null;
}

const m = (n: number) => `$${shortMoneyText(n)}`;
function shortMoneyText(n: number): string {
  const a = Math.abs(n);
  const [d, s] = a >= 1e9 ? [1e9, 'b'] : a >= 1e6 ? [1e6, 'm'] : a >= 1e3 ? [1e3, 'k'] : [1, ''];
  return `${Math.round((n / d) * (d === 1 ? 1 : 100)) / (d === 1 ? 1 : 100)}${s}`;
}

/** One plan in plain words: "Sell LOW’s top block (64m shares, $6.39b) → buy HIGH’s next block ($500m): +$2.49b/yr". */
export function planText(p: Plan): string {
  const spare = p.spare.length ? `spare shares of ${p.spare.map((s) => s.acronym).join(', ')} (${m(p.spare.reduce((t, s) => t + s.money, 0))})` : '';
  const sold = p.sell ? `${p.sell.acronym}’s top block (${shortMoneyText(p.sell.shares)} shares, ${m(p.sell.money)})` : spare;
  const buy = `buy ${p.buys.map((b) => `${b.acronym}’s ${b.blocks === 1 ? 'next block' : `next ${b.blocks} blocks`} (${m(b.cost)})`).join(' + ')}`;
  const gain = `+${m(p.gain)}/yr`;
  const left = p.left >= 1e6 ? `, ${m(p.left)} left over` : '';
  const note = p.note ? ` — ${p.note}` : '';
  if (p.kind === 'wait') {
    const alone = Number.isFinite(p.alone) ? ` (or ~${p.alone} weeks on dividends alone, keeping ${p.sell?.acronym}: +${m(p.buys[0]!.yearly)}/yr)` : '';
    return `Hold ${p.sell?.acronym ?? 'your stocks'} ~${p.weeks} more week${p.weeks === 1 ? '' : 's'} (saving your dividends), then sell ${sold} → ${buy}: ${gain}${note}${alone}`;
  }
  return `Sell ${sold} → ${buy}: ${gain}${left}${note}`;
}

/** What the planner takes for granted (its tooltip). */
export const PLAN_ASSUMPTIONS = [
  'How it works out: a stock pays its benefit once per period for each block you hold (block n needs 2^(n−1) × the shares of the first).',
  'The money from a sale buys the best yearly return for the money first, block after block, while it lasts; the gain is what those pay a year minus what the sold block paid.',
  'Money a year = the benefit’s money × 365 ÷ its period; item benefits at today’s market value (as if you sell them).',
  'Prices are today’s; selling costs Torn’s 0.1% fee. Prices move — check the numbers before you trade.',
  'Benefits without a money value (energy, discounts, passive perks) are never sold — only their spare shares, which pay nothing.',
  '“Hold N weeks”: you keep every money dividend you get until then; your cash on hand isn’t counted (the API doesn’t give it here).',
  'A new block pays its first dividend after one full period; selling a block loses the days it had built up — so sell right after its payout.',
].join('\n');
